import { Form, Modal, Tag, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'ui';
import { Select, SelectOption } from 'ui/Select';
import { InputNumber } from 'ui/InputNumber';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { DatePicker } from 'ui/DatePicker';
import { FulfillmentCardProps, FulfillmentFormProduct } from './types';
import { FallbackImage } from 'ui/UploadImage/fallBack/FallBackImage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { WithoutModalAddressFinder, AddressData } from 'ui/AdressSearch/WithoutModal';
import { Divider } from 'ui/Divider';

export const FulfillmentCard = ({
  fulfillment,
  items,
  getAvailableProducts,
  getRemainingQuantityForProduct,
  getMaxQuantityForProduct,
  onUpdateFulfillment,
  onMarkFulfillmentSent,
  onDeleteFulfillment,
  onSubmitFulfillment,
  onSendFulfillmentInvoice,
  onConfirmFulfillmentPayment,
  onDownloadFulfillmentInvoice,
  canMarkFulfillmentAsSent,
  isProvider = false,
  canSellerAct = false,
}: FulfillmentCardProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const isSent = fulfillment.status === 'sent';
  const hasInvoice = !!fulfillment.invoice_nr;
  const isPaid = fulfillment.billing_status === '1';
  const isDraft = fulfillment.is_draft ?? false;
  const [isSellerEditing, setIsSellerEditing] = useState(false);

  const isBuyerDraftEditing = isDraft && !isProvider;
  const isSellerActiveEditing = !isDraft && canSellerAct && isSellerEditing && !isSent && !isPaid;
  const canEditFields = isBuyerDraftEditing || isSellerActiveEditing;
  const showFieldsRow = canEditFields || (!isDraft && !isSent);

  useEffect(() => {
    const address = fulfillment.delivery_address ?? fulfillment.deliveryAddress ?? '';
    form.setFieldValue('delivery_address', address);
  }, [fulfillment.delivery_address, fulfillment.deliveryAddress, form]);

  const getProductItemById = useCallback(
    (orderItemId: number) => items.find((i) => i.id === orderItemId),
    [items]
  );

  const getProductNameById = useCallback(
    (orderItemId: number): string => getProductItemById(orderItemId)?.product_name ?? '-',
    [getProductItemById]
  );

  const getProductUnitById = useCallback(
    (orderItemId: number): string => {
      const item = items.find((i) => i.id === orderItemId);
      return item?.stock_unit ?? '';
    },
    [items]
  );

  const availableProductsForSelect = useMemo(() => {
    return getAvailableProducts(fulfillment.id);
  }, [fulfillment.id, getAvailableProducts]);

  const initialProducts = useMemo(() => {
    return (
      fulfillment.products?.map((p) => ({
        order_item_id: p.id ?? p.order_item_id,
        product_quantity: Number(p.pivot?.product_quantity ?? p.product_quantity ?? 0),
      })) ?? []
    );
  }, [fulfillment.products]);

  const watchedProducts: FulfillmentFormProduct[] = Form.useWatch('products', form) ?? initialProducts;

  const handleValuesChange = useCallback(
    (changedValues: { products: FulfillmentFormProduct[] }, allValues: { products: FulfillmentFormProduct[] }) => {
      if (!isBuyerDraftEditing) return;

      if (changedValues.products) {
        changedValues.products.forEach((changedProduct, index) => {
          if (changedProduct && changedProduct.order_item_id !== undefined) {
            const maxQty = getMaxQuantityForProduct(fulfillment.id, changedProduct.order_item_id);
            form.setFieldValue(['products', index, 'product_quantity'], maxQty);
          }
        });
      }

      const updatedProducts = form.getFieldValue('products') ?? [];
      const validProducts = updatedProducts.filter(
        (p: FulfillmentFormProduct) => p.order_item_id !== undefined && p.product_quantity > 0
      );
      onUpdateFulfillment(fulfillment.id, { products: validProducts });
    },
    [fulfillment.id, onUpdateFulfillment, form, getMaxQuantityForProduct, isBuyerDraftEditing]
  );

  const handleDateChange = useCallback(
    (date: dayjs.Dayjs) => {
      if (date && isBuyerDraftEditing) {
        onUpdateFulfillment(fulfillment.id, {
          expected_delivery_date: date.format('YYYY-MM-DD'),
        });
      }
    },
    [fulfillment.id, onUpdateFulfillment, isBuyerDraftEditing]
  );

  const handleAddressChange = useCallback(
    (data: AddressData) => {
      if (isBuyerDraftEditing) {
        onUpdateFulfillment(fulfillment.id, { delivery_address: data.address });
      }
    },
    [fulfillment.id, onUpdateFulfillment, isBuyerDraftEditing]
  );

  const handleDeliveryPriceChange = useCallback(
    (value: number | string | undefined | null) => {
      if (value !== null && isBuyerDraftEditing) {
        onUpdateFulfillment(fulfillment.id, { delivery_price: Number(value) });
      }
    },
    [fulfillment.id, onUpdateFulfillment, isBuyerDraftEditing]
  );

  const handleSubmitDraft = useCallback(() => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'fulfillment.confirm_submit' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => onSubmitFulfillment?.(fulfillment.id),
    });
  }, [fulfillment.id, onSubmitFulfillment, intl]);

  const handleSellerSave = useCallback(() => {
    const products = form.getFieldValue('products') ?? [];
    const validProducts = products.filter(
      (p: FulfillmentFormProduct) => p.order_item_id !== undefined && p.product_quantity > 0
    );
    const deliveryPrice = form.getFieldValue('fulfillment_delivery_price');

    onUpdateFulfillment(fulfillment.id, {
      products: validProducts,
      delivery_price: deliveryPrice !== undefined ? Number(deliveryPrice) : undefined,
      delivery_address: form.getFieldValue('delivery_address'),
    });
    setIsSellerEditing(false);
  }, [fulfillment.id, onUpdateFulfillment, form]);

  const handleSend = useCallback(() => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'fulfillment.confirm_send' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => onMarkFulfillmentSent(fulfillment.id),
    });
  }, [fulfillment.id, onMarkFulfillmentSent, intl]);

  const handleDelete = useCallback(() => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'fulfillment.confirm_delete' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => onDeleteFulfillment(fulfillment.id),
    });
  }, [fulfillment.id, onDeleteFulfillment, intl]);

  const handleSendInvoice = useCallback(() => {
    const address = form.getFieldValue('delivery_address');
    const deliveryPrice = form.getFieldValue('fulfillment_delivery_price');

    if (!address) {
      message.error(intl.formatMessage({ id: 'fulfillment.validation.address_required' }));
      return;
    }

    if (deliveryPrice === undefined || deliveryPrice === null || deliveryPrice === '') {
      message.error(intl.formatMessage({ id: 'fulfillment.validation.delivery_price_required' }));
      return;
    }

    Modal.confirm({
      title: intl.formatMessage({ id: 'fulfillment.confirm_send_invoice' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => onSendFulfillmentInvoice?.(fulfillment.id),
    });
  }, [fulfillment.id, onSendFulfillmentInvoice, intl, form]);

  const handleConfirmPayment = useCallback(() => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'fulfillment.confirm_payment_question' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => onConfirmFulfillmentPayment?.(fulfillment.id),
    });
  }, [fulfillment.id, onConfirmFulfillmentPayment, intl]);

  const handleDownloadInvoice = useCallback(() => {
    onDownloadFulfillmentInvoice?.(fulfillment.id);
  }, [fulfillment.id, onDownloadFulfillmentInvoice]);

  const getSelectOptions = useCallback(
    (currentOrderItemId: number | undefined) => {
      const options = [
        ...(currentOrderItemId
          ? [
              {
                value: currentOrderItemId,
                label: getProductNameById(currentOrderItemId),
              },
            ]
          : []),
        ...availableProductsForSelect
          .filter((p) => p.id !== currentOrderItemId)
          .map((p) => ({
            value: p.id,
            label: `${p.product_name} (${getRemainingQuantityForProduct(p.product_id)} ${p.stock_unit})`,
          })),
      ];
      return options;
    },
    [availableProductsForSelect, getProductNameById, getRemainingQuantityForProduct]
  );

  const showDisabledFields = showFieldsRow && !canEditFields;

  const totalBlock = (
    <>
      <span className="total-label">{intl.formatMessage({ id: 'general.total_price' })}</span>
      <span className="total-value">
        {(watchedProducts.reduce((sum, p) => {
          const item = getProductItemById(p?.order_item_id);
          const price = Number(item?.stock_price ?? 0);
          const qty = Number(p?.product_quantity ?? 0);
          return sum + price * qty;
        }, 0) + Number(fulfillment.delivery_price ?? 0)).toFixed(2)} EUR
      </span>
    </>
  );

  return (
    <div className="fulfillment-card">
      {hasInvoice && (
        <div className="fulfillment-invoice-header">
          <span className="invoice-label">{intl.formatMessage({ id: 'transactions.invoice_nr' })}:</span>
          <span className="invoice-number">{fulfillment.invoice_nr}</span>
        </div>
      )}
      <Form form={form} initialValues={{ products: initialProducts }} onValuesChange={handleValuesChange}>
        {showFieldsRow ? (
          <>
            <div className="fulfillment-fields-row">
              <div className="fulfillment-address-field">
                <WithoutModalAddressFinder
                  form={form}
                  name="delivery_address"
                  fullPath={['delivery_address']}
                  label={intl.formatMessage({ id: 'invoice.delivery_address' })}
                  layout="vertical"
                  disabled={showDisabledFields}
                  deliveryTypeValue="delivery"
                  onAddressSelect={handleAddressChange}
                />
              </div>
              <div className="fulfillment-date-field">
                <DatePicker
                  label={intl.formatMessage({ id: 'fulfillment.expected_date' })}
                  layout="vertical"
                  value={
                    fulfillment.expected_delivery_date
                      ? dayjs(fulfillment.expected_delivery_date)
                      : undefined
                  }
                  onChange={handleDateChange}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  disabled={showDisabledFields}
                />
              </div>
              {(!isDraft || isProvider) && (
                <div className="fulfillment-price-field">
                  <InputNumber
                    name="fulfillment_delivery_price"
                    label={intl.formatMessage({ id: 'invoice.delivery_price' })}
                    layout="vertical"
                    min={0}
                    controls={false}
                    addonAfter="EUR"
                    initialValue={fulfillment.delivery_price != null ? Number(fulfillment.delivery_price) : undefined}
                    onChange={handleDeliveryPriceChange}
                    disabled={showDisabledFields}
                  />
                </div>
              )}
            </div>
            <Divider marginTop={30} marginBottom={16} />
          </>
        ) : (
          <>
            <div className="fulfillment-header">
              <div className="header-field">
                <span className="header-field-label">{intl.formatMessage({ id: 'invoice.delivery_address' })}</span>
                <span className="header-field-value">{fulfillment.delivery_address ?? fulfillment.deliveryAddress ?? '-'}</span>
              </div>
              <div className="header-field">
                <span className="header-field-label">{intl.formatMessage({ id: 'fulfillment.expected_date' })}</span>
                <span className="header-field-value">
                  {fulfillment.expected_delivery_date ? dayjs(fulfillment.expected_delivery_date).format('DD.MM.YYYY') : '-'}
                </span>
              </div>
              <div className="header-field">
                <span className="header-field-label">{intl.formatMessage({ id: 'invoice.delivery_price' })}</span>
                <span className="header-field-value">
                  {fulfillment.delivery_price && Number(fulfillment.delivery_price) > 0 ? `${fulfillment.delivery_price} EUR` : '-'}
                </span>
              </div>
            </div>
            <Divider marginTop={16} marginBottom={16} />
          </>
        )}

        <div className="fulfillment-body">
          <Form.List name="products">
            {(fields, { add, remove }) => (
              <>
                {fields.length > 0 ? (
                  <div className="products-list">
                    {fields.map((field, index) => {
                      const product = watchedProducts[index];
                      const orderItemId = product?.order_item_id;

                      if (isSent) {
                        const item = getProductItemById(orderItemId);
                        const qualityMarks = item?.product?.qualitymarks?.filter(
                          (q, i, arr) =>
                            arr.findIndex(
                              (x) => x.quality_mark_id === q.quality_mark_id
                            ) === i
                        );
                        return (
                          <div key={field.key} className="product-row product-row-readonly">
                            <span className="product-mobile-label">
                              {intl.formatMessage({ id: 'general.product' })}
                            </span>
                            <div className="product-row-readonly-content">
                              <div className="product-cell">
                                <span className="product-name">{getProductNameById(orderItemId)}</span>
                                {qualityMarks && qualityMarks.length > 0 && (
                                  <div className="quality-marks">
                                    {qualityMarks.map((qmark) => (
                                      <FallbackImage
                                        key={qmark.quality_mark_id}
                                        className="quality-mark"
                                        src={qmark.quality_mark?.logo || ''}
                                        type="notFound"
                                        preview={false}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className="product-quantity">
                                {product?.product_quantity} {getProductUnitById(orderItemId)}
                              </span>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={field.key} className="product-row">
                          <div className="product-select-item">
                            <Select
                              name={[field.name, 'order_item_id']}
                              label={intl.formatMessage({ id: 'general.product' })}
                              placeholder={intl.formatMessage({ id: 'delivery.select_product' })}
                              disabled={showDisabledFields}
                            >
                              {getSelectOptions(orderItemId).map((opt) => (
                                <SelectOption key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectOption>
                              ))}
                            </Select>
                          </div>

                          {orderItemId && (
                            <div className="quantity-input-item">
                              <InputNumber
                                name={[field.name, 'product_quantity']}
                                label={intl.formatMessage({ id: 'general.amount_table' })}
                                min={0.01}
                                max={getMaxQuantityForProduct(fulfillment.id, orderItemId)}
                                addonAfter={getProductUnitById(orderItemId)}
                                disabled={showDisabledFields}
                              />
                            </div>
                          )}

                          {!showDisabledFields && (
                            <>
                              <Button
                                icon={<CloseOutlined />}
                                onClick={() => remove(field.name)}
                                size="small"
                                className="delete-product-btn"
                              />
                              <div className="delete-product-btn-mobile">
                                <span onClick={() => remove(field.name)}>
                                  {intl.formatMessage({ id: 'fulfillment.delete_product' })}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-products">
                    {intl.formatMessage({ id: 'fulfillment.no_products' })}
                  </div>
                )}

                {canEditFields && (
                    <Button
                      onClick={() => add({ order_item_id: undefined, product_quantity: 1 })}
                      disabled={availableProductsForSelect.length === 0}
                      className="add-product-btn"
                      label={intl.formatMessage({ id: 'fulfillment.add_product' })}
                    />
                )}
              </>
            )}
          </Form.List>
          <div className="fulfillment-total fulfillment-total-mobile">{totalBlock}</div>
        </div>

        <Divider marginTop={16} marginBottom={16} />
        <div className="fulfillment-actions">
          <div className="fulfillment-actions-left">
            {hasInvoice && !isPaid && canSellerAct && (
              <Button
                type="primary"
                onClick={handleConfirmPayment}
                label={intl.formatMessage({ id: 'fulfillment.confirm_payment' })}
              />
            )}

            {hasInvoice && isPaid && (
              <Tag color="green" className="payment-status-tag">
                {intl.formatMessage({ id: 'payment.invoice_paid' })}
              </Tag>
            )}

            {hasInvoice && !isPaid && !isProvider && (
              <Tag color="orange" className="payment-status-tag">
                {intl.formatMessage({ id: 'payment.invoice_not_paid' })}
              </Tag>
            )}
          </div>

          <div className="fulfillment-total fulfillment-total-desktop">{totalBlock}</div>

          <div className="fulfillment-actions-right">
            {!isProvider && !hasInvoice && !isSent && (
              <Button
                onClick={handleDelete}
                danger
                label={intl.formatMessage({ id: 'general.delete' })}
              />
            )}

            {isDraft && !isProvider && (
              <Button
                type="primary"
                onClick={handleSubmitDraft}
                disabled={!canMarkFulfillmentAsSent(fulfillment)}
                label={intl.formatMessage({ id: 'fulfillment.submit' })}
              />
            )}

            {!isDraft && !isSent && !isPaid && !hasInvoice && canSellerAct && !isSellerEditing && (
              <Button
                onClick={() => setIsSellerEditing(true)}
                label={intl.formatMessage({ id: 'fulfillment.edit' })}
              />
            )}

            {isSellerActiveEditing && (
              <Button
                type="primary"
                onClick={handleSellerSave}
                label={intl.formatMessage({ id: 'fulfillment.save' })}
              />
            )}

            {!isDraft && !isSent && !isPaid && canSellerAct && (
              <Button
                onClick={handleDelete}
                danger
                label={intl.formatMessage({ id: 'general.delete' })}
              />
            )}

            {!isDraft && !isSent && !isPaid && canSellerAct && !hasInvoice && (
              <Button
                type="primary"
                onClick={handleSendInvoice}
                disabled={!canMarkFulfillmentAsSent(fulfillment)}
                label={intl.formatMessage({ id: 'fulfillment.send_invoice' })}
              />
            )}

            {hasInvoice && (
              <Button
                onClick={handleDownloadInvoice}
                label={intl.formatMessage({ id: 'fulfillment.download_invoice' })}
              />
            )}

            {hasInvoice && (canSellerAct || isSent) && (
              <Button
                type="primary"
                onClick={handleSend}
                disabled={isSent}
                label={intl.formatMessage({ id: isSent ? 'fulfillment.sent' : 'fulfillment.send' })}
              />
            )}
          </div>
        </div>
      </Form>
    </div>
  );
};
